import type { Column } from "../../Table.types";
import type { FilterTypes, FilterValue } from "../TableFilters.type";

import {
  Button,
  ButtonSizes,
  ButtonVariants
} from "@certa/catalyst/components/Button";
import { Popover } from "@certa/catalyst/components/Popover";
import { PopperPlacements } from "@certa/catalyst/components/types";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Filter } from "@certa/icons/components/Filter";

import { TableFilter } from "../Filters";

import styles from "./FilterOverlay.module.css";
import { classNames } from "@certa/catalyst/utils/common";
import {
  FilterOverlayProvider,
  useFilterOverlay
} from "./FilterOverlayContext";

type FilterOverlayProps<TableData, FilterType extends FilterTypes> = {
  column: Column<TableData>;
  onSearch: (value: FilterValue<FilterType>) => void;
  onReset: () => void;
  isManualFiltering: boolean;
};

const FilterOverlayComponent = <FilterType extends FilterTypes>() => {
  const {
    activeColumn,
    handleSearch,
    handleReset,
    isFilterVisible,
    setIsFilterVisible,
    isSearchDisabled,
    isResetDisabled
  } = useFilterOverlay();

  const { filterType } = activeColumn.columnDef.meta as {
    filterType: FilterType;
  };

  const columnFilterValue =
    activeColumn.getFilterValue() as FilterValue<FilterType>;

  if (!filterType) return null;

  return (
    <Popover
      placement={PopperPlacements.BOTTOM_START}
      openInPortal={true}
      visible={isFilterVisible}
      onVisibleChange={setIsFilterVisible}
      onClose={() => setIsFilterVisible(false)}
      content={
        <Stack
          dangerouslySetClassName={styles.filterPopover}
          padding="12"
          gap="12"
        >
          <TableFilter type={filterType} />
          <Stack direction="horizontal" justify="space-between" align="center">
            <Button
              disabled={isSearchDisabled}
              variant={ButtonVariants.FILLED}
              size={ButtonSizes.SMALL}
              onClick={handleSearch}
              htmlType="button"
            >
              Search
            </Button>
            <Button
              disabled={isResetDisabled}
              size={ButtonSizes.SMALL}
              onClick={handleReset}
              htmlType="button"
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      }
    >
      <button
        className={classNames({
          [styles.filterIcon]: true,
          [styles.filterIconActive]: Array.isArray(columnFilterValue)
            ? columnFilterValue.length > 0
            : !!columnFilterValue,
          [styles.filterIconOpen]: isFilterVisible
        })}
        onClick={() => setIsFilterVisible(prevState => !prevState)}
        type="button"
      >
        <Filter size={12} />
      </button>
    </Popover>
  );
};

const FilterOverlay = <
  TableData extends Record<string, any>,
  FilterType extends FilterTypes
>(
  props: FilterOverlayProps<TableData, FilterType>
) => {
  const { column, onSearch, onReset, isManualFiltering } = props;

  return (
    <FilterOverlayProvider
      column={column}
      isManualFiltering={isManualFiltering}
      onSearch={onSearch}
      onReset={onReset}
    >
      <FilterOverlayComponent />
    </FilterOverlayProvider>
  );
};

export { FilterOverlayComponent, FilterOverlay };
